import { createContext, type ReactNode, useContext, useEffect, useRef } from "react";

import { runInAction } from "mobx";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { getUser } from "@synthesis/shared-auth";
import { getApiBaseUrl } from "@synthesis/shared-services";
import { logger } from "@synthesis/shared-utils";

import { SourceType, getDirectories, type DirectoryListResponse, type FileMetaDto } from "../api";

import { useFileSystemWorker, useStores } from "../store";
import { DocumentsTypes } from "../types";
import { inferUploadDocumentType } from "../utils/file-tab-filter";
import {
  applyUploadProgressToFiles,
  createTempFileMeta,
  mergeServerFilesWithUploadProgress,
  normalizeBaseName,
  pendingUploadToFileMeta,
  removeUploadProgressForFile,
  type FileMetaWithProgress,
} from "../utils/upload-progress";

type UploadVariables = { files: File[]; uploadType?: "ifc" | "pdf" };
type UploadContext = { previousFilesData: unknown };
type VersionUpdateVariables = { oldFile: FileMetaDto; newFile: File };

type UploadProgressContextType = {
  progress: { [fileId: string]: number };
  totalProgress: number;
  uploading: boolean;
  filesCount: number;
  completedFiles: number;
  startUpload: (files: File[], uploadType?: "ifc" | "pdf") => void;
  versionUpdate: (oldFile: FileMetaDto, newFile: File) => void;
};

const UploadProgressContext = createContext<UploadProgressContextType | undefined>(undefined);

export const useUploadProgress = () => {
  const context = useContext(UploadProgressContext);
  if (!context) {
    throw new Error("useUploadProgress must be used within an UploadProgressProvider");
  }
  return context;
};

export const UploadProgressProvider = ({ children }: { children: ReactNode }) => {
  const { projectId } = useParams({ strict: false });
  const { directoryStore, generalStore, fileStore } = useStores();
  const queryClient = useQueryClient();
  const { workerStore } = useFileSystemWorker();
  const workerMessageHandlerRef = useRef<(event: MessageEvent) => void | undefined>(undefined);

  // При входе/возврате в проект восстанавливаем temp-строки из workerStore,
  // чтобы не терять загруженные/загружаемые файлы до появления их в getDirectories.
  useEffect(() => {
    if (!projectId) return;
    runInAction(() => {
      const pending = workerStore.getPendingForProject(projectId);
      const pendingRows = Object.values(pending)
        .filter((p) => p?.baseName && p.status === "progress")
        .map((p) => pendingUploadToFileMeta(projectId, p));

      if (pendingRows.length === 0) return;

      fileStore.files = mergeServerFilesWithUploadProgress(fileStore.files, pendingRows).toSorted(generalStore.getSortFn());
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restore pending uploads only when project changes
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;

    const handler = (event: MessageEvent) => {
      const { type, fileName, percent, currentProjectId, fileSize } = event.data;

      if (type === "progress") {
        if (currentProjectId !== projectId) return;

        runInAction(() => {
          const rawName = typeof fileName === "string" ? fileName : "";
          const normalizedFileName = normalizeBaseName(rawName);
          const { type: documentType, ext: documentExt } = inferUploadDocumentType(rawName);

          fileStore.files = applyUploadProgressToFiles(fileStore.files, rawName, normalizedFileName, percent, () =>
            createTempFileMeta({
              id: rawName,
              baseName: normalizedFileName,
              sourceId: projectId,
              size: fileSize,
              type: documentType,
              progress: percent,
              ext: documentExt,
            }),
          ).toSorted(generalStore.getSortFn());
        });
      } else if (event.data.type === "success") {
        const response = JSON.parse(event.data.response);
        const responseFileId = response.fileMeta?.id || response.id;
        workerStore.setUploadedFileIds({ oldFileId: event.data.oldFileId, newFileId: responseFileId });
        workerStore.updateGanttModel({
          modelId: event.data.oldFileId,
          projectId,
          newModelId: responseFileId,
          typeEvent: event.data.typeEvent,
        });
        workerStore.setStatus("success");
        workerStore.updateProgress(event.data.fileName, 100);
        workerStore.setCompletedFiles(workerStore.completedFiles + 1);

        (async () => {
          try {
            if (!directoryStore.rootFolderId) return;
            const responseData = (await getDirectories({ path: { id: directoryStore.rootFolderId } })).data;
            const item = (responseData as DirectoryListResponse[]).find((x) => x?.fileMeta?.id === responseFileId);
            const realMeta = item?.fileMeta;
            if (!realMeta) return;

            const merged: FileMetaDto = {
              ...realMeta,
              version: Math.max(Number(realMeta.version ?? 1), 1),
            };

            runInAction(() => {
              const normalizedFileName = normalizeBaseName(String(event.data.fileName || ""));
              const typeEvent = String(event.data.typeEvent || "");
              const oldFileId = event.data.oldFileId;

              let nextFiles = fileStore.files.filter((f) => {
                const withProgress = f as FileMetaWithProgress;
                if (typeof withProgress.progress !== "number") return true;
                return normalizeBaseName(f.baseName) !== normalizedFileName;
              });

              if (typeEvent === "updateVersion" && typeof oldFileId === "string" && oldFileId) {
                const idxOld = nextFiles.findIndex((f) => f.id === oldFileId);
                if (idxOld >= 0) {
                  nextFiles[idxOld] = merged;
                } else {
                  nextFiles = [...nextFiles, merged];
                }
              } else {
                const idxTemp = nextFiles.findIndex(
                  (f) => normalizeBaseName(f.baseName) === normalizedFileName && typeof (f as FileMetaWithProgress).progress === "number",
                );
                if (idxTemp >= 0) nextFiles[idxTemp] = merged;
                else nextFiles = [...nextFiles, merged];
              }

              nextFiles = nextFiles.filter((f, idx, arr) => arr.findIndex((x) => x.id === f.id) === idx);

              fileStore.files = nextFiles.toSorted(generalStore.getSortFn());
              workerStore.clearPendingForProjectBaseName(projectId, normalizedFileName);
            });

            generalStore.enrichDocsFiles([merged]);
          } catch (e) {
            logger.error("replace temp file with real meta failed", e);
          }
        })();
      } else if (type === "error") {
        workerStore.setStatus("error");
        const { error, fileName: errorFileName } = event.data;
        workerStore.updateProgress(errorFileName, 100);
        workerStore.setCompletedFiles(workerStore.completedFiles + 1);
        logger.error("Upload error:", error);

        runInAction(() => {
          const normalizedFileName = normalizeBaseName(String(errorFileName || ""));
          fileStore.files = removeUploadProgressForFile(fileStore.files, normalizedFileName, fileStore.allFiles).toSorted(generalStore.getSortFn());

          workerStore.clearPendingForProjectBaseName(projectId, normalizedFileName);
        });

        void generalStore.getContents(null);
      }
    };

    workerMessageHandlerRef.current = handler;
    workerStore.addMessageListener(handler);

    return () => {
      workerStore.removeMessageListener(handler);
    };
  }, [projectId, directoryStore.rootFolderId, generalStore, fileStore, workerStore]);

  const uploadMutation = useMutation<void, Error, UploadVariables, UploadContext>({
    mutationFn: async ({ files, uploadType }) => {
      if (!projectId) {
        throw new Error("projectId is required");
      }

      const filesArray = Array.from(files);
      workerStore.setFilesCount(filesArray.length);
      workerStore.setCompletedFiles(0);
      workerStore.totalProgress = 0;
      workerStore.progress = {};
      workerStore.setUploading(true);

      const isPdfUpload = uploadType === "pdf";

      filesArray.forEach((file) => {
        let body;
        const apiBaseUrl = getApiBaseUrl();

        if (isPdfUpload) {
          body = {
            sourceType: SourceType.PROJECT_ID,
            sourceId: projectId,
            parentId: directoryStore.rootFolderId,
            document: file,
          };
        } else {
          body = {
            sourceType: SourceType.PROJECT_ID,
            sourceId: projectId,
            parentId: directoryStore.rootFolderId,
            document: file,
          };
        }

        const url = isPdfUpload ? apiBaseUrl + "/api/files/pdf/uploadWithPreview" : apiBaseUrl + "/api/files/document";
        const token = getUser()?.access_token;
        const typeEvent = "upload";
        const devUrl = import.meta.env.DEV ? url.replace(apiBaseUrl, "") : url;

        // oxlint-disable-next-line unicorn/require-post-message-target-origin -- Worker.postMessage has no targetOrigin parameter
        workerStore.postMessage({ file, url: devUrl, body, token, typeEvent, currentProjectId: projectId });
      });
    },
    onMutate: async ({ files, uploadType }) => {
      if (!projectId) return { previousFilesData: undefined };

      const previousFilesData = queryClient.getQueryData(["files", projectId, directoryStore.rootFolderId]);
      const filesArray = Array.from(files);
      const isPdfUpload = uploadType === "pdf";
      const tempFiles = filesArray.map((file) => {
        const ext = file.name.split(".").pop() || "";
        return createTempFileMeta({
          id: file.name,
          baseName: normalizeBaseName(file.name),
          sourceId: projectId,
          size: file.size,
          type: isPdfUpload ? DocumentsTypes.pdf_document : DocumentsTypes.model,
          progress: 0,
          ext: isPdfUpload ? ext : "ifc",
        });
      });
      runInAction(() => {
        fileStore.files = mergeServerFilesWithUploadProgress(fileStore.files, tempFiles).toSorted(generalStore.getSortFn());
      });
      return { previousFilesData };
    },
    onError: (error, _variables, context) => {
      logger.error("Upload failed:", error);
      if (context?.previousFilesData) {
        queryClient.setQueryData(["files", projectId, directoryStore.rootFolderId], context.previousFilesData);
      }
      runInAction(() => {
        workerStore.clearFiles();
      });
    },
  });

  const updateVersionMutation = useMutation<void, Error, VersionUpdateVariables>({
    mutationFn: async ({ oldFile, newFile }) => {
      if (!projectId) {
        throw new Error("projectId is required");
      }

      workerStore.setCompletedFiles(0);
      workerStore.totalProgress = 0;
      workerStore.progress = {};
      workerStore.setUploading(true);
      const body = {
        document: newFile,
        id: oldFile.id,
      };
      const apiBaseUrl = getApiBaseUrl();
      const url = apiBaseUrl + "/api/files/document/update";
      const token = getUser()?.access_token;
      const typeEvent = "updateVersion";
      const devUrl = import.meta.env.DEV ? url.replace(apiBaseUrl, "") : url;

      /* oxlint-disable unicorn/require-post-message-target-origin -- Worker.postMessage has no targetOrigin parameter */
      workerStore.postMessage({
        file: newFile,
        url: devUrl,
        body,
        token,
        typeEvent,
        currentProjectId: projectId,
        oldFileId: oldFile.id,
      });
      /* oxlint-enable unicorn/require-post-message-target-origin */
    },
    onError: (error) => {
      logger.error("Upload failed:", error);
      runInAction(() => {
        workerStore.clearFiles();
        workerStore.clearProgress();
      });
    },
  });

  const startUpload = (files: File[], uploadType?: "ifc" | "pdf") => {
    uploadMutation.mutate({ files, uploadType });
  };
  const versionUpdate = (oldFile: FileMetaDto, newFile: File) => {
    updateVersionMutation.mutate({ oldFile, newFile });
  };

  return (
    <UploadProgressContext.Provider
      value={{
        progress: workerStore.progress,
        totalProgress: workerStore.totalProgress,
        uploading: workerStore.uploading,
        filesCount: workerStore.filesCount,
        completedFiles: workerStore.completedFiles,
        startUpload,
        versionUpdate,
      }}
    >
      {children}
    </UploadProgressContext.Provider>
  );
};
